import React, { useState } from 'react';
import { Form, Input, DatePicker, Radio, Row, Col, Typography, Divider, Select, Button, Modal, Checkbox } from 'antd';
import dayjs from 'dayjs';
import countryList from 'react-select-country-list';
import { useSettings } from "../../contexts/settingsContext";
import { RightOutlined } from '@ant-design/icons';
import { loadState } from '../../stores/store';
import { createContestant } from '../../api/contestant';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { Option } = Select;

const CreateContestant = ({ submitting, enforceAgeRestriction = true }) => {
    const [form] = Form.useForm();
    const [allergies, setAllergies] = useState(false);
    const [isIdNumberEntered, setIsIdNumberEntered] = useState(false);
    const [isPassportNumberEntered, setIsPassportNumberEntered] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const { state: settings } = useSettings();
    const user = loadState().user;
    const navigate = useNavigate();

    const validateSouthAfricanIdNumber = (_, value) => {
        if (value.length !== 13) {
            return Promise.reject(new Error('ID number must be 13 digits long'));
        }
        const year = parseInt(value.slice(0, 2), 10);
        const month = parseInt(value.slice(2, 4), 10);
        const day = parseInt(value.slice(4, 6), 10);
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return Promise.reject(new Error('Invalid date in ID number'));
        }
        const gender = parseInt(value.slice(6, 10), 10);
        if (gender < 0 || gender > 9999) {
            return Promise.reject(new Error('Invalid gender digits in ID number'));
        }
        const citizenship = parseInt(value.slice(10, 11), 10);
        if (citizenship !== 0 && citizenship !== 1) {
            return Promise.reject(new Error('Invalid citizenship digit in ID number'));
        }
        const luhnCheck = (num) => {
            let sum = 0;
            for (let i = 0; i < num.length; i++) {
                let digit = parseInt(num[i], 10);
                if (i % 2 === num.length % 2) {
                    digit *= 2;
                    if (digit > 9) {
                        digit -= 9;
                    }
                }
                sum += digit;
            }
            return sum % 10 === 0;
        };
        if (!luhnCheck(value)) {
            return Promise.reject(new Error('Invalid digit in ID number. Please verify your ID number is correct.'));
        }
        return Promise.resolve();
    };

    const handleDateChange = (date) => {
        if (date) {
            const age = dayjs().diff(date, 'year');
            if (enforceAgeRestriction && age < 18) {
                form.setFields([
                    {
                        name: 'date_of_birth',
                        errors: ['You must be at least 18 years old'],
                    },
                ]);
            } else {
                form.setFieldsValue({ age });
            }
        }
    };

    const handleIdNumberChange = (e) => {
        const value = e.target.value;
        setIsIdNumberEntered(!!value);
        if (selectedCountry === 'South Africa' && value.length === 13) {
            const year = parseInt(value.slice(0, 2), 10);
            const month = parseInt(value.slice(2, 4), 10) - 1;
            const day = parseInt(value.slice(4, 6), 10);
            const currentYear = dayjs().year();
            const fullYear = year + (year > currentYear % 100 ? 1900 : 2000);
            const dateOfBirth = dayjs(new Date(fullYear, month, day));
            const age = dayjs().diff(dateOfBirth, 'year');
            form.setFieldsValue({ date_of_birth: dateOfBirth, age });
        }
    };

    const handlePassportNumberChange = (e) => {
        const value = e.target.value;
        setIsPassportNumberEntered(!!value);
    };

    const handleCountryChange = (value) => {
        setSelectedCountry(value);
        form.setFieldsValue({ id_number: '', passport_number: '' });
        setIsIdNumberEntered(false);
        setIsPassportNumberEntered(false);
    };

    const handleAllergiesChange = (e) => {
        const value = e.target.value;
        setAllergies(value === true);
        form.setFieldsValue({ allergies: value });
    };

    const onFinish = async (values) => {
      values.first_name = user.first_name;
      values.last_name = user.last_name;
      values.email = user.email;
      values.cell_number = user.cell_number;
      values.work_number = user.work_number;

      const formattedValues = {
                          first_name: values.first_name,
                          last_name: values.last_name,
                          contestant_number: values.contestant_number || undefined,
                          address: {
                              street_1: values['address.street_1'],
                              street_2: values['address.street_2'],
                              city: values['address.city'],
                              country: values['address.country'],
                              province: values['address.province'],
                              postal_code: values['address.postal_code']
                          },
                          gender: values.gender,
                          id_number: values.id_number || "0",
                          date_of_birth: values.date_of_birth ? dayjs(values.date_of_birth).format('YYYY-MM-DD') : null,
                          passport_number: values.passport_number || "0",
                          age: values.age,
                          cell_number: values.cell_number,
                          work_number: values.work_number,
                          email: values.email,
                          ice_contact_name: values.ice_contact_name,
                          relationship_to_ice: values.relationship_to_ice,
                          ice_contact_email: values.ice_contact_email,
                          allergies: values.allergies || false,
                          allergies_specification: values.allergies_specification,
                          created_by: user._id,
                          province: user.activeProvince
                      };
      
      try {
        await createContestant(formattedValues);
        navigate('/applications', {replace:true});
      } catch (error) {
        console.error(error);
      }     
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Form
                form={form}
                className='contestant_form'
                name="contestant"
                initialValues={{ remember: true, age: null }}
                layout="vertical"
                onFinish={onFinish}
                scrollToFirstError
            >
        <Col span={24}>
            <Title level={4}>Personal Information</Title>
        </Col>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.country"
                            label="Country of Citizenship"
                            rules={[{ required: true, message: 'Please input your country of citizenship!' }]}
                        >
                            <Select
                                placeholder="Select Country"
                                onChange={handleCountryChange}
                                value={selectedCountry}
                                showSearch
                                optionFilterProp="children"
                                autoComplete="none"
                            >
                                {countryList().getData().map(country => (
                                    <Option key={country.value} value={country.label}>{country.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    {selectedCountry && (
                        <>
                            {selectedCountry === 'South Africa' && (
                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item
                                        name="id_number"
                                        label="ID Number"
                                        rules={[{ required: !isPassportNumberEntered, message: 'Please input your ID number!' }, { validator: selectedCountry === 'South Africa' ? validateSouthAfricanIdNumber : null }]}
                                    >
                                        <Input placeholder="ID Number" onChange={handleIdNumberChange} />
                                    </Form.Item>
                                </Col>
                            )}
                            {(selectedCountry !== 'South Africa' || settings.passportRequired) && (
                                <Col xs={24} sm={24} md={12}>
                                    <Form.Item
                                        name="passport_number"
                                        label="Passport Number"
                                        rules={[{ required: !isIdNumberEntered, message: 'Please input your passport number!' }]}
                                    >
                                        <Input placeholder="Passport Number" onChange={handlePassportNumberChange} />
                                    </Form.Item>
                                </Col>
                            )}
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="date_of_birth"
                                    label="Date of Birth"
                                    rules={[{ required: !isIdNumberEntered, message: 'Please select your date of birth!' }]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        onChange={handleDateChange}
                                        disabledDate={current => enforceAgeRestriction && current && current > dayjs().subtract(18, 'year')}
                                        disabled={isIdNumberEntered || selectedCountry === 'South Africa'}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                    name="age"
                                    label="Age"
                                    rules={[{ required: true, message: 'Please input your age!' }]}>
                                    <Input placeholder="Age" disabled />
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24}>
                        <Form.Item
                            name="gender"
                            label="Gender"
                            rules={[{ required: true, message: 'Please select your gender!' }]}
                        >
                            <Radio.Group options={[
                                { label: 'Male', value: 'Male' },
                                { label: 'Female', value: 'Female' },
                            ]} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={24}>
                        <Form.Item
                            name="allergies"
                            label="Do you Have Any Allergies?"
                            rules={[{ required: true, message: 'Please select if you have any allergies!' }]}
                        >
                            <Radio.Group onChange={handleAllergiesChange} options={[
                                { label: 'Yes', value: true },
                                { label: 'No', value: false }
                            ]} />
                        </Form.Item>
                    </Col>
                    {allergies && (
                        <Col xs={24} sm={24} md={24}>
                            <Form.Item
                                name="allergies_specification"
                                label="Allergies Specification"
                                rules={[{ required: allergies, message: 'Please specify your allergies!' }]}
                            >
                                <Input placeholder="Allergies Specification" />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
                <Divider />
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Title level={4}>Address Information</Title>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.street_1"
                            label="Street 1"
                            rules={[{ required: true, message: 'Please input your street!' }]}
                        >
                            <Input placeholder="Street 1" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.street_2"
                            label="Street 2"
                        >
                            <Input placeholder="Street 2" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.street_3"
                            label="Street 3"
                        >
                            <Input placeholder="Street 3" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.city"
                            label="City"
                            rules={[{ required: true, message: 'Please input your city!' }]}
                        >
                            <Input placeholder="City" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.province"
                            label="Province/State"
                            rules={[{ required: true, message: 'Please input your province/state!' }]}
                        >
                            <Input placeholder="Province/State" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="address.postal_code"
                            label="Postal Code"
                            rules={[{ required: true, message: 'Please input your postal code!' }]}
                        >
                            <Input placeholder="Postal Code" />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider />
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Title level={4}>Emergency Contact Information</Title>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="ice_contact_name"
                            label="ICE Contact Name"
                            rules={[{ required: true, message: 'Please input your ICE contact name!' }]}
                        >
                            <Input placeholder="ICE Contact Name" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="relationship_to_ice"
                            label="Relationship to ICE"
                            rules={[{ required: true, message: 'Please input your relationship to ICE!' }]}
                        >
                            <Input placeholder="Relationship to ICE" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item
                            name="ice_contact_email"
                            label="ICE Contact Email"
                            rules={[{ required: true, message: 'Please input your ICE contact email!' }]}
                        >
                            <Input placeholder="ICE Contact Email" />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Row>
                        <Col span={24} style={{ textAlign: 'right' }}>
                            <Button type="primary" loading={submitting} icon={<RightOutlined />} htmlType="submit">
                                Create Contestant
                            </Button>
                        </Col>
                    </Row>
                </div>
            </Form>
        </div>
    );
};

export default CreateContestant;  
